import { TOP_SYMBOL_CHIP_ID, TOP_SYMBOL_PRICE_LINE_ID } from "testIds";
import Text from "common/Typography/Text";
import React, { useMemo } from "react";
import { useTheme } from "styled-components";
import styled from "styled-components";
import IconWithFallback from "Global/IconWithFallback";
import { Market } from "types";
import WatchlistPrice from "features/watchlist/common/WatchlistPrice";
import { ICON_TYPES } from "@shared/common/constants";
import LivePriceChange from "common/LiveComponents/LivePriceChange";
import { getCoinExtraDetailsRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import Tooltip from "common/Tooltips/Tooltip";
import {
  NEW_LOOK_TICKER_WIDTH,
  SYMBOL_FONT_SIZE,
  SYMBOL_FONT_WEIGHT,
  SYMBOL_HORIZONTAL_MARGIN,
  SYMBOL_ICON_MARGIN,
  SYMBOL_ICON_SIZE,
} from "features/watchlist/TopSymbols/constants.ts";

interface TopSymbolsProps {
  market: Market;
  symbolFontSize?: number;
  symbolFontWeight?: number;
}

const TopSymbol: React.FC<TopSymbolsProps> = ({ market }) => {
  const appTheme = useTheme();
  const { symbol, exchange } = market;
  const { historyPush } = useHistoryWithQuery();

  const openCoinExtraDetailsDrawer = () => {
    historyPush(getCoinExtraDetailsRoute(location.pathname, symbol));
  };

  const watchlistPriceStyle = useMemo(() => {
    return {
      fontWeight: appTheme.typography.fontWeights.SEMI_BOLD,
      margin: "3px 0 1px 0",
    };
  }, [appTheme]);

  if (appTheme.isNewLook) {
    const [base, quote] = symbol.split("/");
    return (
      <Tooltip
        title={exchange}
        styles={{ root: { fontSize: appTheme.typography.fontSizes.S } }}
      >
        <NewLookChip
          data-testid={TOP_SYMBOL_CHIP_ID}
          onClick={openCoinExtraDetailsDrawer}
        >
          <IconWithFallback
            coinOrExchangeName={symbol}
            iconStyle={{
              width: NEW_LOOK_CHIP_ICON_SIZE,
              height: NEW_LOOK_CHIP_ICON_SIZE,
            }}
            iconType={ICON_TYPES.COIN}
          />
          <NewLookLines>
            <NewLookSymbol>
              {base}
              {quote && <NewLookQuote>/{quote}</NewLookQuote>}
            </NewLookSymbol>
            <NewLookPriceLine data-testid={TOP_SYMBOL_PRICE_LINE_ID}>
              <WatchlistPrice
                market={market}
                fontSize="12px"
                textColor={appTheme.colors.primaryText}
                style={NEW_LOOK_PRICE_STYLE}
                hideCurrencyIcon={true}
              />
              <LivePriceChange
                symbol={symbol}
                exchange={exchange}
                initialChange={market.graph_data.change}
                size={11}
              />
            </NewLookPriceLine>
          </NewLookLines>
        </NewLookChip>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={exchange}
      styles={{ root: { fontSize: appTheme.typography.fontSizes.S } }}
    >
      <Wrapper onClick={openCoinExtraDetailsDrawer}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <IconWithFallback
            coinOrExchangeName={symbol}
            iconStyle={{
              marginRight: SYMBOL_ICON_MARGIN,
              width: SYMBOL_ICON_SIZE,
              height: SYMBOL_ICON_SIZE,
            }}
            iconType={ICON_TYPES.COIN}
          />
          <Text
            type="secondary"
            style={{
              fontSize: SYMBOL_FONT_SIZE,
              fontWeight: SYMBOL_FONT_WEIGHT,
            }}
          >
            {symbol}
          </Text>
        </div>
        <div style={{ width: "fit-content" }}>
          <WatchlistPrice
            market={market}
            fontSize={appTheme.typography.fontSizes.S}
            textColor={appTheme.colors.primaryText}
            style={watchlistPriceStyle}
          />
        </div>
        <LivePriceChange
          symbol={symbol}
          exchange={exchange}
          initialChange={market.graph_data.change}
          size={appTheme.typography.pxFontSizes.XS}
        />
      </Wrapper>
    </Tooltip>
  );
};

export default TopSymbol;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 ${SYMBOL_HORIZONTAL_MARGIN}px;
  cursor: pointer;
  max-width: 148px;
`;
export const NEW_LOOK_CHIP_ICON_SIZE = 18;
const NEW_LOOK_PRICE_STYLE = {
  fontWeight: 500,
  fontVariantNumeric: "tabular-nums",
} as const;
// Two short lines (symbol; price and change) keep five chips in the top bar.
const NewLookChip = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  width: ${NEW_LOOK_TICKER_WIDTH}px;
  flex: none;
  padding: 5px 8px 5px 6px;
  border-radius: 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 150ms;
  &:hover {
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
`;
const NewLookLines = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
  line-height: 16px;
`;
const NewLookSymbol = styled.span`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 12px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const NewLookQuote = styled.span`
  color: ${(p) => p.theme.colors.lightestText};
  font-weight: ${(p) => p.theme.typography.fontWeights.NORMAL};
`;
const NewLookPriceLine = styled.span`
  display: grid;
  grid-template-columns: minmax(0, max-content) max-content;
  justify-content: start;
  align-items: center;
  gap: 5px;
  font-variant-numeric: tabular-nums;
  > :first-child {
    min-width: 0;
    overflow: hidden;
  }
  > :last-child {
    justify-self: start;
  }
`;
